import React from "react";
import clsx from "clsx";
import { LoadingSpinner } from "./LoadingSpinner";

export type ButtonVariant =
  | "default"
  | "dark"
  | "success"
  | "transparent"
  | "highlighted"
  | "secondary"
  | "tertiary"
  | "icon"
  | "iconSecondary"
  | "text";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  disabled?: boolean;
  children?: React.ReactNode;
  className?: string;
  icon?: React.ReactNode;
  fullWidth?: boolean;
  size?: "small" | "medium" | "large" | "xLarge" | "chip" | "cta";
  isLoading?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  variant = "default",
  disabled = false,
  children,
  className,
  icon,
  fullWidth = false,
  size = "medium",
  isLoading = false,
  ...props
}) => {
  const baseStyles = clsx(
    "font-inter inline-flex items-center justify-center gap-1.5 transition-all duration-200 focus:outline-none rounded-full font-medium",
    // A text button has no surface to lift, so the shadow lands on nothing and
    // reads as a stray rectangle — most visibly on the full-width accordion
    // headers, where it shadows the whole row.
    variant !== "text" && "hover:scale-[1.02] hover:shadow-md active:scale-95",
    fullWidth && "w-full",
  );

  const iconSizeClasses = {
    small: "w-8 h-8",
    medium: "w-10 h-10",
    large: "w-12 h-12",
    xLarge: "w-14 h-14",
    chip: "w-8 h-8",
    cta: "w-10 h-10",
  };

  const sizeClasses = {
    small: "px-button-x-sm py-button-y-sm text-button-xs",
    medium: "px-button-x-lg py-button-y-lg text-button-xs",
    large: "px-button-x-lg py-button-y-xl text-button-md",
    xLarge: "px-button-x-lg py-button-y-lg text-button-md",
    chip: "h-8 px-3 py-1 text-xs font-medium",
    cta: "h-10 px-5 text-base font-semibold",
  };

  // A loading button is disabled at the DOM level so it can't be pressed
  // twice, but it must not *look* disabled: fading the whole thing to 10%
  // takes the spinner with it, leaving a smudge where the CTA was. Only a
  // genuinely unavailable button gets the faded treatment.
  const looksDisabled = disabled && !isLoading;

  const variantStyles = {
    default: clsx(
      "bg-primary text-on-primary font-semibold",
      "hover:bg-primary-hover",
      "focus:ring-2 focus:ring-offset-2 focus:ring-primary",
      looksDisabled && "opacity-10",
    ),
    dark: clsx(
      "bg-primary text-on-primary font-medium",
      "hover:bg-primary/90",
      "focus:ring-2 focus:ring-offset-2 focus:ring-primary",
      looksDisabled && "opacity-20",
    ),
    success: clsx(
      "bg-status-success text-on-status font-semibold",
      "hover:bg-primary-hover",
      "focus:ring-2 focus:ring-offset-2 focus:ring-primary",
      looksDisabled && "opacity-10",
    ),
    transparent: clsx(
      "bg-primary-soft text-ink font-medium",
      "hover:bg-primary-soft-hover",
      "focus:ring-2 focus:ring-offset-2 focus:ring-primary",
      looksDisabled && "opacity-10",
    ),
    highlighted: clsx(
      "bg-accent-soft text-accent font-medium",
      "hover:bg-accent-soft-hover",
      "focus:ring-2 focus:ring-offset-2 focus:ring-accent",
      looksDisabled && "opacity-20",
    ),
    secondary: clsx(
      "bg-surface-card text-ink border border-border font-medium",
      "hover:bg-surface-page",
      "focus:ring-2 focus:ring-offset-2 focus:ring-primary",
      looksDisabled && "opacity-20",
    ),
    tertiary: clsx(
      "text-ink flex flex-col items-center font-medium",
      'after:content-[""] after:w-full after:h-px after:bg-ink after:mt-0.5',
      "hover:opacity-70",
      "focus:ring-2 focus:ring-offset-2 focus:ring-primary",
      looksDisabled && "opacity-20",
    ),
    icon: clsx(
      "bg-primary text-on-primary inline-flex items-center justify-center p-0",
      "hover:bg-primary-hover",
      "focus:ring-2 focus:ring-offset-2 focus:ring-primary",
      looksDisabled && "opacity-10",
    ),
    iconSecondary: clsx(
      "bg-surface-card text-ink border border-border inline-flex items-center justify-center p-0",
      "hover:bg-surface-page",
      "focus:ring-2 focus:ring-offset-2 focus:ring-primary",
      looksDisabled && "opacity-20",
    ),
    text: clsx(
      "text-ink font-medium underline",
      "hover:opacity-70",
      "focus:outline-none",
      looksDisabled && "opacity-20",
    ),
  };

  // On the button itself the spinner has to contrast with the button, not with
  // the page it used to hang over.
  const spinnerTheme: "light" | "dark" =
    variant === "transparent" ||
    variant === "secondary" ||
    variant === "tertiary" ||
    variant === "text"
      ? "dark"
      : "light";

  if (variant === "icon" || variant === "iconSecondary") {
    return (
      <button
        className={clsx(baseStyles, variantStyles[variant], className)}
        disabled={disabled || isLoading}
        {...props}
      >
        <span
          className={clsx(
            "flex items-center justify-center p-0",
            iconSizeClasses[size],
          )}
        >
          {icon}
        </span>
      </button>
    );
  }

  return (
    <button
      className={clsx(
        baseStyles,
        sizeClasses[size],
        variantStyles[variant],
        className,
      )}
      disabled={disabled || isLoading}
      {...props}
    >
      {/* The spinner sits ON the label, not beside it: parked outside the
          content box (`left-full`) it fell into the button's padding and was
          clipped by the rounded edge. Centred over faded text it also keeps the
          button's width steady, so a row of buttons doesn't shift while one
          loads. */}
      <div className="relative flex items-center gap-1.5">
        {icon && !isLoading && <span>{icon}</span>}
        <span
          className={clsx(
            "flex items-center gap-1.5 transition-opacity duration-150",
            isLoading && "opacity-30",
          )}
        >
          {children}
        </span>
        {isLoading && (
          <span className="absolute inset-0 flex items-center justify-center">
            <LoadingSpinner theme={spinnerTheme} />
          </span>
        )}
      </div>
    </button>
  );
};
